import {
    AccrualMatchAuditItemType,
    AccrualMatchAuditSnapshot,
} from "@common/schemas/domains/accrualReview/accrual-match-audit-log.schema";
import { ApproveAccrualReviewBody } from "@common/schemas/domains/accrualReview/accrual-review.schema";

/**
 * Reconstruct an approve-accrual-review request body from an audit-log
 * snapshot of a previously-removed match. Used by the History drilldown
 * "redo match" affordance to re-create the same match the user just undid.
 *
 * Returns `null` when the snapshot has no items we can re-link (e.g. a
 * cleared snapshot or a malformed payload).
 */
export function buildApproveBodyFromSnapshot(
    snapshot: AccrualMatchAuditSnapshot,
): ApproveAccrualReviewBody | null {
    const transactionId = snapshot.transaction.transactionId;
    const invoiceIds = new Set<string>();
    const billIds = new Set<string>();
    let matchedTransactionId: number | null = null;

    for (const item of snapshot.items) {
        if (item.type === AccrualMatchAuditItemType.INVOICE_LINE_ITEM) {
            invoiceIds.add(item.documentId);
        } else if (item.type === AccrualMatchAuditItemType.BILL_LINE_ITEM) {
            billIds.add(item.documentId);
        } else if (item.type === AccrualMatchAuditItemType.TRANSACTION) {
            matchedTransactionId = item.transactionId;
        }
    }

    if (
        matchedTransactionId == null &&
        invoiceIds.size === 0 &&
        billIds.size === 0
    ) {
        return null;
    }

    // The approve schema accepts `matchedTransactionId` together with
    // `invoiceIds` / `billIds` (provided no `lineItemAllocations` are set),
    // so we forward every collected id rather than dropping documents when
    // a paired transaction is also present.
    return {
        transactionId,
        ...(matchedTransactionId != null ? { matchedTransactionId } : {}),
        ...(invoiceIds.size > 0 ? { invoiceIds: [...invoiceIds] } : {}),
        ...(billIds.size > 0 ? { billIds: [...billIds] } : {}),
    };
}
